import { Controller } from "@hotwired/stimulus"
import { activateFocusTrap, deactivateFocusTrap } from "./utils/focus_trap"
import { applyEnter, animateOut } from "./utils/motion"

// Dialog component. Triggered by any element with `data-dialog-trigger="<id>"`.
// Listens for clicks on the trigger via document-level delegation so the
// trigger can live anywhere in the DOM.
//
// A11y (WAI-ARIA APG Dialog):
//   - Focus trap: Tab/Shift+Tab ciclam dentro do dialog
//   - Restaura foco ao trigger no close
//   - Esc fecha (via document listener)
//   - Inert nos siblings ao abrir
//
// Motion: painel faz fade+scale (animate-dialog-in/out), backdrop faz fade
// (animate-backdrop-in/out). Ao fechar, espera a animação de saída terminar
// antes de esconder. Tudo respeita `prefers-reduced-motion` via utils/motion
// (a espera é pulada e o CSS global zera a duração das animações).
export default class extends Controller {
  static targets = ["root", "panel", "backdrop"]
  static values  = { id: String }

  connect() {
    this.boundOpen = (e) => {
      const trigger = e.target.closest(`[data-dialog-trigger="${this.idValue}"]`)
      if (trigger) {
        e.preventDefault()
        this.open()
      }
    }
    this.boundKeydown = (e) => {
      if (e.key === "Escape" && !this.rootTarget.classList.contains("hidden")) {
        this.close()
      }
    }
    document.addEventListener("click", this.boundOpen)
    document.addEventListener("keydown", this.boundKeydown)
  }

  disconnect() {
    document.removeEventListener("click", this.boundOpen)
    document.removeEventListener("keydown", this.boundKeydown)
    if (this.trap) deactivateFocusTrap(this.trap)
  }

  open() {
    this.rootTarget.classList.remove("hidden")
    this.rootTarget.classList.add("flex")
    document.body.style.overflow = "hidden"
    if (this.hasPanelTarget) applyEnter(this.panelTarget, "animate-dialog-in")
    if (this.hasBackdropTarget) applyEnter(this.backdropTarget, "animate-backdrop-in")
    this.trap = activateFocusTrap(this.rootTarget)
  }

  async close() {
    // Anima a saída do painel e do backdrop em paralelo antes de esconder.
    // animateOut resolve imediatamente sob prefers-reduced-motion.
    await Promise.all([
      this.hasPanelTarget ? animateOut(this.panelTarget, "animate-dialog-out") : null,
      this.hasBackdropTarget ? animateOut(this.backdropTarget, "animate-backdrop-out") : null
    ])
    this.closeNow()
  }

  // Fecha SÍNCRONO, sem animação — libera o focus trap e o overflow na hora.
  // Usado quando algo precisa do trap removido imediatamente (ex.: seguir um link
  // que abre OUTRO dialog ou navega), sem esperar a animação de saída.
  closeNow() {
    this.rootTarget.classList.add("hidden")
    this.rootTarget.classList.remove("flex")
    document.body.style.overflow = ""
    deactivateFocusTrap(this.trap)
    this.trap = null
  }
}
